import { useEffect, useRef, useState, type FormEvent } from "react";
import { Locale } from "./copy";

export function parseNumericDraft(text: string, min: number, max: number, decimals = false): number | null {
  // Empty, signs, exponents, whitespace and fractional integers never coerce to zero.
  if (!(decimals ? /^\d+(?:\.\d{0,2})?$/ : /^\d+$/).test(text)) return null;
  const value = Number(text);
  return Number.isFinite(value) && value >= min && value <= max && (decimals || Number.isSafeInteger(value)) ? value : null;
}

/** Raw text is independent of the last valid model value. Invalid drafts block the form. */
export function NumericDraftInput({ value, min, max, decimals = false, commitOnBlur = false, locale, onValue }: {
  value: number; min: number; max: number; decimals?: boolean; commitOnBlur?: boolean; locale: Locale; onValue: (value: number) => void;
}): React.JSX.Element {
  const [text, setText] = useState(String(value));
  const editing = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  const parsed = parseNumericDraft(text, min, max, decimals);
  const error = locale === "es" ? `Introduzca ${decimals ? "un número con hasta dos decimales" : "un número entero"} entre ${min} y ${max}.` : `Enter ${decimals ? "a number with up to two decimal places" : "a whole number"} from ${min} to ${max}.`;
  useEffect(() => { if (!editing.current) setText(String(value)); }, [value]);
  useEffect(() => { input.current?.setCustomValidity(parsed === null ? error : ""); }, [parsed, error]);
  const change = (event: FormEvent<HTMLInputElement>): void => {
    editing.current = true;
    const raw = event.currentTarget.value;
    const next = parseNumericDraft(raw, min, max, decimals);
    setText(raw);
    event.currentTarget.setCustomValidity(next === null ? error : "");
    if (next !== null && !commitOnBlur) onValue(next);
  };
  return <input ref={input} type="text" inputMode={decimals ? "decimal" : "numeric"}
    autoComplete="off" required value={text} aria-invalid={parsed === null}
    onFocus={() => { editing.current = true; }} onInput={change} onChange={change}
    onKeyDown={event => {
      if (event.key === "Enter" && commitOnBlur && parsed !== null && parsed !== value) {
        event.preventDefault(); // Commit the structural change before a later form submission.
        onValue(parsed); event.currentTarget.blur();
      }
    }}
    onBlur={() => {
      editing.current = false;
      if (parsed !== null) { if (commitOnBlur) onValue(parsed); setText(String(parsed)); }
    }} />;
}
